<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    loading-message="Creating Customers..."
    button-location="top"
    no-action
  >
    <template #title>
      Registration by CSV
    </template>

    <template #top-form>
      <input
        ref="fileInput"
        type="file"
        accept=".csv"
        style="display: none"
        @change="handleFileChange"
      >

      <div class="form-submit-button-group mb-20">
        <p-button
          type="info"
          round
          class="mr-30"
          :disabled="isLoading"
          @click="openCSV"
        >
          Upload CSV
        </p-button>
        <p-button
          type="purple"
          round
          :disabled="isLoading"
          @click="downloadCSV"
        >
          Download CSV Example
        </p-button>
      </div>
    </template>

    <template #content>
      <hr>
      <h6 style="text-transform: none">
        Below is an example of CSV data. The highlighted rows contain issues that need to be fixed:
      </h6>
      <ul class="text-red">
        <li><strong>Row 4</strong>: Missing <strong>last name</strong>.</li>
        <li><strong>Row 5</strong>: Missing <strong>first name</strong>.</li>
        <li>
          <strong>Row 6</strong>: Missing both <strong>phone number</strong> and <strong>home number</strong>. At least one of these is
          required.
        </li>
        <li><strong>Row 7</strong>: Contains an <strong>invalid email address</strong>.</li>
        <li>
          <strong>Row 8</strong>: Has an <strong>incorrect birthday format</strong>. The correct format is <strong>YYYY-MM-DD</strong>.
        </li>
        <li><strong>Row 9</strong>: Missing <strong>address information</strong>. All address fields are required.</li>
      </ul>
      <hr>

      <table-view-container
        :page-id="pageId"
        :data="exampleData"
        :table-columns="tableColumns"
        :highlight-list="highlightList"
        :height="600"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref } from 'vue';

import util from '@/Utils/util';
import { sendPost } from '@/Utils/api';
import { usePartner } from '@/use/partner';
import { useSwalDialog } from '@/use/swalDialog';
import { TableColumnType } from '@/models/table';

const pageId = 'registration-by-csv';
const demoCSVUrl = 'http://df9khbjwrdn51.cloudfront.net/Partner_Callers_Demo.csv';

const tableColumns: TableColumnType[] = [
  { label: 'First Name', field: 'firstName', width: 100 },
  { label: 'Last Name', field: 'lastName', width: 100 },
  { label: 'Phone Number', field: 'cellPhone', width: 130 },
  { label: 'Home Number', field: 'homeNumber', width: 120 },
  { label: 'Email', field: 'email', width: 200 },
  { label: 'Birthday', field: 'dob', width: 90 },
  { label: 'Street Address', field: 'streetAddress', width: 120 },
  { label: 'City', field: 'city', width: 100 },
  { label: 'State', field: 'state', width: 70 },
  { label: 'Zip', field: 'zip', width: 80 },
];

const exampleData = [
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '+12295955901',
    homeNumber: '+12295955902',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-08-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '+12295955903',
    homeNumber: '',
    email: '',
    dob: '',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '',
    homeNumber: '+12295955904',
    email: 'shang.tao812@hotmail.com',
    dob: '',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: '',
    cellPhone: '+12295955901',
    homeNumber: '+12295955902',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-08-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: '',
    lastName: 'Jin',
    cellPhone: '+12295955901',
    homeNumber: '+12295955902',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-08-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '',
    homeNumber: '',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-08-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '+12295955901',
    homeNumber: '+12295955902',
    email: 'shang',
    dob: '1987-08-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '+12295955901',
    homeNumber: '',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-8-12',
    streetAddress: '2601 Garcia Avenue',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
  {
    firstName: 'ShangTao',
    lastName: 'Jin',
    cellPhone: '+12295955901',
    homeNumber: '+12295955902',
    email: 'shang.tao812@hotmail.com',
    dob: '1987-08-12',
    streetAddress: '',
    city: 'Mountain View',
    state: 'CA',
    zip: '94043',
  },
];

const highlightList = [
  {
    type: 'row',
    index: 3,
    class: 'text-red',
  },
  {
    type: 'row',
    index: 4,
    class: 'text-red',
  },
  {
    type: 'row',
    index: 5,
    class: 'text-red',
  },
  {
    type: 'row',
    index: 6,
    class: 'text-red',
  },
  {
    type: 'row',
    index: 7,
    class: 'text-red',
  },
  {
    type: 'row',
    index: 8,
    class: 'text-red',
  },
];

const { partnerCode } = usePartner();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const fileInput: Ref<HTMLElement | null> = ref(null);
const isLoading = ref(false);

const handleFileChange = async (event: any) => {
  const file = event.target.files[0];

  if (!file || file.type !== 'text/csv') return;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('codeName', partnerCode.value);

  isLoading.value = true;
  try {
    await sendPost('partner/callers-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    showSuccessPopupDialog({
      text: "This may take a little while to process. We'll send you an email as soon as it's ready!",
    });
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const downloadCSV = () => {
  window.open(demoCSVUrl, '_blank');
};

const openCSV = () => {
  fileInput.value?.click();
};
</script>
